import type { EditChange, JoinConflict } from '@rollcall/contracts'
import type { TFunction } from 'i18next'
import { slotDictKey } from '../slots.js'
import { useT } from './i18n-context.js'
import { contentName } from './labels.js'

/**
 * Разбор несоответствий персонажа кампании (D61).
 *
 * Персонаж не подходит столу по нескольким причинам сразу: уровень
 * не тот, запись, которой в кампании нет, свой контент, который
 * никто не подтверждал. Раньше это были разные отказы в разных
 * местах — и мастер узнавал про вторую причину, починив первую.
 *
 * Здесь они собраны в один список, и по каждому пункту сказано
 * человеческими словами: что не так, чем это грозит и какие выходы.
 * Выходы — не кнопки на каждый пункт: решение одно на заявку,
 * и оно принимается ниже. Пункты объясняют, из чего выбирать.
 *
 * Понижения уровня среди выходов нет: снять уровни значит отменить
 * чужие выборы.
 */

function describe(
  t: TFunction,
  conflict: JoinConflict,
): {
  readonly what: string
  readonly risk: string
  readonly ways: string
} {
  if (conflict.kind === 'level') {
    const above = conflict.characterLevel > conflict.campaignLevel
    return {
      what: t('campaigns.review.level.what', {
        character: conflict.characterLevel,
        campaign: conflict.campaignLevel,
      }),
      risk: above
        ? t('campaigns.review.level.riskAbove')
        : t('campaigns.review.level.riskBelow'),
      ways: above
        ? t('campaigns.review.level.waysAbove')
        : t('campaigns.review.level.waysBelow'),
    }
  }

  const names = conflict.refs.map((ref) => ref.name).join(', ')
  if (conflict.kind === 'missing-content') {
    return {
      what: t('campaigns.review.missing.what', { names }),
      risk: t('campaigns.review.missing.risk'),
      ways: t('campaigns.review.missing.ways'),
    }
  }
  if (conflict.kind === 'custom-disabled') {
    return {
      what: t('campaigns.review.disabled.what', { names }),
      risk: t('campaigns.review.disabled.risk'),
      ways: t('campaigns.review.disabled.ways'),
    }
  }
  return {
    what: t('campaigns.review.unapproved.what', { names }),
    risk: t('campaigns.review.unapproved.risk'),
    ways: t('campaigns.review.unapproved.ways'),
  }
}

export function JoinReview({
  conflicts,
  onRaiseLevel,
}: {
  readonly conflicts: readonly JoinConflict[]
  /**
   * Поднять уровень кампании до уровня персонажа — один из выходов
   * по пункту про уровень (D61). `null` — выход недоступен: уровень
   * персонажа ниже, и поднимать нечего.
   */
  readonly onRaiseLevel: ((level: number) => void) | null
}) {
  const t = useT()
  if (conflicts.length === 0) return null

  const level = conflicts.find((item) => item.kind === 'level')
  const above =
    level !== undefined &&
    level.kind === 'level' &&
    level.characterLevel > level.campaignLevel

  return (
    <div className="app-review" data-testid="join-review">
      <p className="app-field-label">{t('campaigns.review.title')}</p>
      <ul className="app-list">
        {conflicts.map((conflict) => {
          const said = describe(t, conflict)
          return (
            <li
              key={conflict.kind}
              className="app-review-item"
              data-conflict={conflict.kind}
            >
              <p className="app-value-note">{said.what}</p>
              <p className="app-field-hint">{said.risk}</p>
              <p className="app-field-hint">{said.ways}</p>
            </li>
          )
        })}
      </ul>
      {above && onRaiseLevel !== null && level.kind === 'level' && (
        <div className="app-actions">
          <button
            type="button"
            className="app-button app-button-quiet"
            onClick={() => {
              onRaiseLevel(level.characterLevel)
            }}
          >
            {t('campaigns.review.raise', { level: level.characterLevel })}
          </button>
        </div>
      )}
    </div>
  )
}

/** Свои строки правки: не слоты плана, а поля самого визарда. */
const OWN_LINES = new Set([
  'name',
  'class',
  'species',
  'background',
  'ability_method',
  'abilities',
  'ability_increase',
])

function lineTitle(t: TFunction, slot: string): string {
  if (OWN_LINES.has(slot)) return t(`edit.line.${slot}`)
  return t(`slot.${slotDictKey(slot)}`, { source: '', defaultValue: slot })
}

/** Значение строки правки человеческими словами. */
function valueText(
  t: TFunction,
  slot: string,
  item: EditChange['before'][number],
): string {
  if (slot === 'abilities' || slot === 'ability_increase') {
    const [ability = '', amount = ''] = item.value.split(':')
    return `${t(`abilitiesShort.${ability}`, { defaultValue: ability })} ${amount}`
  }
  if (slot === 'ability_method') {
    return t(`wizard.abilities.${item.value}`, { defaultValue: item.value })
  }
  if (item.value.startsWith('srd:') || item.value.startsWith('u:')) {
    return contentName(t, item.value, item.name)
  }
  return item.name
}

/**
 * Что меняет правка (D62): по строке на слот, было и стало.
 *
 * Мастер решает по тому, что читает. Пропавшее показано пустым
 * «стало», а не убрано: правка, снимающая выбор, должна быть видна
 * так же, как добавляющая.
 */
export function EditChanges({ changes }: { readonly changes: readonly EditChange[] }) {
  const t = useT()
  if (changes.length === 0) {
    return <p className="app-field-hint">{t('edit.noChanges')}</p>
  }
  const join = (slot: string, items: EditChange['before']) =>
    items.length === 0
      ? t('edit.empty')
      : items.map((item) => valueText(t, slot, item)).join(', ')
  return (
    <div className="app-review" data-testid="edit-changes">
      <p className="app-field-label">{t('edit.changesTitle')}</p>
      <ul className="app-list">
        {changes.map((change) => (
          <li key={change.slot} className="app-review-item" data-change={change.slot}>
            <p className="app-value-note">{lineTitle(t, change.slot)}</p>
            <p className="app-field-hint">
              {t('edit.was', { value: join(change.slot, change.before) })}
            </p>
            <p className="app-field-hint">
              {t('edit.will', { value: join(change.slot, change.after) })}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}
